import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, ShoppingCart, Loader, Star, MapPin, Clock, Navigation, TrendingUp, Award } from 'lucide-react';
import { SEARCH_URL, SEARCH_SUGGESTIONS_URL, CART_ADD_URL, CART_VIEW_URL } from '../../config/api';
import { getFirstImageUrl, authenticatedFetch, FOOD_FALLBACK_IMG } from '../Utils/apiHelpers';
import { getStoredCoords } from '../Utils/location';
import { formatFoodCategory } from '../Utils/category';
import { trackFood } from '../Utils/analytics';
import FoodDetailModal from '../FoodDetailModal/FoodDetailModal';
import './SearchBar.css';

interface Suggestion {
  type: string;
  food_id?: number;
  name: string;
  image?: string;
}

interface SearchFood {
  food_id: number;
  food_name: string;
  description?: string;
  price: number;
  category?: string;
  image_urls?: string | string[];
  average_rating?: number;
  review_count?: number;
  available_quantity?: number;
  stock_status?: string;
  badges?: string[];
  distance_km?: number;
  eta?: string;
  delivery_status?: string;
  trending_score?: number;
  rating_score?: number;
  is_available?: boolean;
  kitchen_name?: string;
  kitchen_id?: number;
  kitchen?: {
    kitchen_id: number;
    kitchen_name: string;
    city?: string;
  };
}

interface SearchKitchen {
  kitchen_id: number;
  kitchen_name: string;
  city?: string;
  address?: string;
  image_url?: string;
  average_rating?: number;
  distance_km?: number;
}

interface SearchMeta {
  total_foods: number;
  total_kitchens: number;
}

interface SearchBarProps {
  onCartChange?: () => void;
  showNotification?: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  onKitchenSelect?: (kitchenId: number, kitchenName: string) => void;
}

const FALLBACK_IMG = FOOD_FALLBACK_IMG;

const RADIUS_OPTIONS = [5, 10, 15, 20, 30];

const SearchBar: React.FC<SearchBarProps> = ({ onCartChange, showNotification, onKitchenSelect }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchFood[] | null>(null);
  const [kitchenResults, setKitchenResults] = useState<SearchKitchen[]>([]);
  const [searchMeta, setSearchMeta] = useState<SearchMeta | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [addingId, setAddingId] = useState<number | null>(null);
  const [detailTarget, setDetailTarget] = useState<number | null>(null);
  const isLoggedIn = !!localStorage.getItem('authToken');

  const [filters, setFilters] = useState({
    category: '',
    city: '',
    min_price: '',
    max_price: '',
    sort_by: '',
    radius: '10',
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) { setSuggestions([]); return; }
    try {
      const res = await fetch(`${SEARCH_SUGGESTIONS_URL}?q=${encodeURIComponent(q.trim())}`, {
        headers: { accept: 'application/json' },
      });
      if (!res.ok) return;
      const data = await res.json();
      setSuggestions((data.suggestions || []).slice(0, 6));
    } catch {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(query);
      setShowSuggestions(true);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, fetchSuggestions]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const performSearch = async (q: string = query, overrideFilters = filters) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setIsSearching(true);
    setShowSuggestions(false);

    const params = new URLSearchParams();
    params.set('query', trimmed);
    if (overrideFilters.category) params.set('category', overrideFilters.category);
    if (overrideFilters.city) params.set('city', overrideFilters.city);
    if (overrideFilters.min_price) params.set('min_price', overrideFilters.min_price);
    if (overrideFilters.max_price) params.set('max_price', overrideFilters.max_price);
    if (overrideFilters.sort_by) params.set('sort_by', overrideFilters.sort_by);

    const coords = getStoredCoords();
    if (coords) {
      params.set('user_lat', String(coords.lat));
      params.set('user_lon', String(coords.lon));
      params.set('radius', overrideFilters.radius || '10');
    }

    try {
      const res = await fetch(`${SEARCH_URL}?${params.toString()}`, {
        headers: { accept: 'application/json' },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setResults(data.foods || []);
      setKitchenResults(data.kitchens || []);
      setSearchMeta({ total_foods: data.total_foods ?? (data.foods || []).length, total_kitchens: data.total_kitchens ?? (data.kitchens || []).length });
      setShowResults(true);
      trackFood('search', trimmed);
    } catch {
      setResults([]);
      setShowResults(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') performSearch();
    if (e.key === 'Escape') { setShowSuggestions(false); setShowResults(false); }
  };

  const handleSuggestionClick = (s: Suggestion) => {
    const foodName = s.name;
    setQuery(foodName);
    setShowSuggestions(false);
    performSearch(foodName);
  };

  const handleResultClick = (food: SearchFood) => {
    setDetailTarget(food.food_id);
  };

  const handleAddToCart = async (e: React.MouseEvent, food: SearchFood) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      showNotification?.('info', 'Login Required', 'Please log in to add items to your cart.');
      return;
    }
    setAddingId(food.food_id);
    try {
      const cartRes = await authenticatedFetch(CART_VIEW_URL);
      if (cartRes.ok) {
        const cartData = await cartRes.json();
        if (cartData.success && cartData.items?.length > 0) {
          const existingKitchen = cartData.items[0]?.kitchen_id;
          const foodKitchen = food.kitchen_id || food.kitchen?.kitchen_id;
          if (existingKitchen && foodKitchen && existingKitchen !== foodKitchen) {
            showNotification?.('warning', 'Different Kitchen', 'Clear your current cart to order from this kitchen.');
            setAddingId(null);
            return;
          }
        }
      }
      const res = await authenticatedFetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ food_id: food.food_id, quantity: 1 }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        trackFood('add_to_cart', food.food_name);
        onCartChange?.();
        showNotification?.('success', 'Added', `${food.food_name} added to cart.`);
      } else {
        showNotification?.('error', 'Failed', data.message || 'Could not add to cart.');
      }
    } catch {
      showNotification?.('error', 'Network Error', 'Please check your connection.');
    } finally {
      setAddingId(null);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setResults(null);
    setKitchenResults([]);
    setSearchMeta(null);
    setShowResults(false);
    setShowSuggestions(false);
  };

  const getKitchenName = (food: SearchFood) => food.kitchen_name || food.kitchen?.kitchen_name || '';

  return (
    <div className="sb-container" ref={containerRef}>
      {/* Search input row */}
      <div className="sb-input-row">
        <div className="sb-input-wrap">
          <Search size={18} className="sb-icon" />
          <input
            type="text"
            className="sb-input"
            placeholder="Search for dishes, cuisines, kitchens..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            aria-label="Search"
          />
          {query && (
            <button className="sb-clear" onClick={clearSearch} aria-label="Clear">
              <X size={16} />
            </button>
          )}
        </div>

        <button
          className="sb-search-btn"
          onClick={() => performSearch()}
          disabled={isSearching || !query.trim()}
        >
          {isSearching ? <span className="sb-spinner" /> : <Search size={18} />}
          <span className="sb-search-label">Search</span>
        </button>
      </div>

      {/* Filters — always visible inline */}
      <div className="sb-filters">
        <div className="sb-filters-row">
          <div className="sb-filter-group">
            <select
              className="sb-filter-input"
              value={filters.category}
              onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
              aria-label="Category"
            >
              <option value="">All Categories</option>
              <option value="veg">Vegetarian</option>
              <option value="non-veg">Non-Vegetarian</option>
            </select>
          </div>
          <div className="sb-filter-group">
            <input
              type="text"
              className="sb-filter-input"
              placeholder="City"
              value={filters.city}
              onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
              aria-label="City"
            />
          </div>
          <div className="sb-filter-group">
            <input
              type="number"
              className="sb-filter-input"
              min="0"
              placeholder="Min Price"
              value={filters.min_price}
              onChange={e => setFilters(f => ({ ...f, min_price: e.target.value }))}
              aria-label="Minimum price"
            />
          </div>
          <div className="sb-filter-group">
            <input
              type="number"
              className="sb-filter-input"
              min="0"
              placeholder="Max Price"
              value={filters.max_price}
              onChange={e => setFilters(f => ({ ...f, max_price: e.target.value }))}
              aria-label="Maximum price"
            />
          </div>
          <div className="sb-filter-group">
            <select
              className="sb-filter-input"
              value={filters.sort_by}
              onChange={e => setFilters(f => ({ ...f, sort_by: e.target.value }))}
              aria-label="Sort by"
            >
              <option value="">Sort: Default</option>
              <option value="latest">Latest</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="top_rated">Top Rated</option>
            </select>
          </div>
          <div className="sb-filter-group">
            <select
              className="sb-filter-input"
              value={filters.radius}
              onChange={e => setFilters(f => ({ ...f, radius: e.target.value }))}
              aria-label="Radius"
            >
              {RADIUS_OPTIONS.map(r => (
                <option key={r} value={String(r)}>{r} km</option>
              ))}
            </select>
          </div>
          <button
            className="sb-filter-clear"
            onClick={() => setFilters({ category: '', city: '', min_price: '', max_price: '', sort_by: '', radius: '10' })}
          >
            Clear
          </button>
          <button className="sb-filter-apply" onClick={() => performSearch()}>
            Apply &amp; Search
          </button>
        </div>
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && !showResults && (
        <ul className="sb-suggestions">
          {suggestions.map((s, i) => (
            <li key={i} className="sb-suggestion-item" onClick={() => handleSuggestionClick(s)}>
              <div className="sb-sug-img-wrap">
                {s.image && s.image !== 'string' ? (
                  <img src={s.image} alt={s.name} className="sb-sug-img" onError={e => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }} />
                ) : (
                  <div className="sb-sug-img-placeholder"><Search size={14} /></div>
                )}
              </div>
              <span className="sb-sug-name">{s.name}</span>
              <span className="sb-sug-type">{s.type}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Search results — in-page flow */}
      {showResults && results !== null && (
        <div className="sb-results">
          <div className="sb-results-header">
            <span className="sb-results-count">
              {searchMeta && searchMeta.total_foods === 0 && searchMeta.total_kitchens === 0
                ? 'No results found'
                : `${searchMeta?.total_foods ?? results.length} food${(searchMeta?.total_foods ?? results.length) === 1 ? '' : 's'}${searchMeta && searchMeta.total_kitchens > 0 ? ` · ${searchMeta.total_kitchens} kitchen${searchMeta.total_kitchens === 1 ? '' : 's'}` : ''} for "${query}"`}
            </span>
            <button className="sb-results-close" onClick={() => setShowResults(false)}><X size={16} /></button>
          </div>
          {kitchenResults.length > 0 && (
            <div className="sb-kitchen-results">
              <h4 className="sb-kitchen-results-title">Kitchens</h4>
              <div className="sb-kitchen-results-grid">
                {kitchenResults.map(k => (
                  <div
                    key={k.kitchen_id}
                    className="sb-kitchen-card"
                    onClick={() => { onKitchenSelect?.(k.kitchen_id, k.kitchen_name); setShowResults(false); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter') { onKitchenSelect?.(k.kitchen_id, k.kitchen_name); setShowResults(false); } }}
                  >
                    <img
                      src={k.image_url && k.image_url !== 'string' ? k.image_url : FALLBACK_IMG}
                      alt={k.kitchen_name}
                      className="sb-kitchen-card-img"
                      onError={e => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }}
                    />
                    <div className="sb-kitchen-card-info">
                      <span className="sb-kitchen-card-name">{k.kitchen_name}</span>
                      {(k.city || k.address) && (
                        <span className="sb-kitchen-card-addr"><MapPin size={10} /> {k.city || k.address}</span>
                      )}
                      {k.distance_km !== undefined && k.distance_km !== null && (
                        <span className="sb-kitchen-card-dist"><Navigation size={10} /> {k.distance_km.toFixed(1)} km</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {results.length > 0 && (
            <ul className="sb-results-list">
              {results.map(food => {
                const isAdding = addingId === food.food_id;
                const soldOut = food.available_quantity === 0 || food.stock_status === 'Sold Out';
                const kitchenName = getKitchenName(food);
                const hasRating = food.average_rating !== undefined && food.average_rating > 0;
                return (
                  <li
                    key={food.food_id}
                    className="sb-result-item"
                    onClick={() => handleResultClick(food)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter') handleResultClick(food); }}
                  >
                    <div className="sb-result-img-wrap">
                      <img
                        src={getFirstImageUrl(food.image_urls)}
                        alt={food.food_name}
                        className="sb-result-img"
                        onError={e => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }}
                      />
                    </div>
                    <div className="sb-result-info">
                      <p className="sb-result-name">{food.food_name}</p>
                      {kitchenName && <p className="sb-result-kitchen"><MapPin size={10} /> {kitchenName}</p>}
                      {food.description && <p className="sb-result-desc">{food.description.slice(0, 80)}{food.description.length > 80 ? '...' : ''}</p>}
                      <div className="sb-result-meta">
                        {food.category && <span className="sb-result-tag">{formatFoodCategory(food.category)}</span>}
                        {hasRating && (
                          <span className="sb-result-tag sb-result-tag--rating">
                            <Star size={10} fill="currentColor" /> {food.average_rating!.toFixed(1)}
                            {food.review_count !== undefined && food.review_count > 0 && ` (${food.review_count})`}
                          </span>
                        )}
                        {food.trending_score !== undefined && food.trending_score > 0 && (
                          <span className="sb-result-tag sb-result-tag--trend"><TrendingUp size={10} /> {Math.round(food.trending_score)}</span>
                        )}
                        {food.rating_score !== undefined && food.rating_score > 0 && (
                          <span className="sb-result-tag sb-result-tag--rscore"><Award size={10} /> {Math.round(food.rating_score)}</span>
                        )}
                        {food.distance_km !== undefined && food.distance_km !== null && (
                          <span className="sb-result-tag sb-result-tag--distance">
                            <Navigation size={10} /> {food.distance_km.toFixed(1)} km
                          </span>
                        )}
                        {food.eta && <span className="sb-result-tag sb-result-tag--eta"><Clock size={10} /> {food.eta}</span>}
                        {food.delivery_status && (
                          <span className="sb-result-tag sb-result-tag--delivery">{food.delivery_status}</span>
                        )}
                        {food.badges?.map(b => (
                          <span key={b} className="sb-result-tag sb-result-tag--badge">{b}</span>
                        ))}
                        {food.stock_status && !soldOut && food.stock_status !== 'In Stock' && (
                          <span className="sb-result-tag sb-result-tag--stock">{food.stock_status}</span>
                        )}
                        {soldOut && <span className="sb-result-tag sb-result-tag--out">Sold Out</span>}
                        {!soldOut && food.available_quantity !== undefined && food.stock_status === 'In Stock' && (
                          <span className="sb-result-tag sb-result-tag--instock">In Stock</span>
                        )}
                      </div>
                    </div>
                    <div className="sb-result-right">
                      <span className="sb-result-price">£{food.price}</span>
                      {isLoggedIn && !soldOut && (
                        <button
                          className="sb-add-btn"
                          onClick={e => handleAddToCart(e, food)}
                          disabled={isAdding}
                          title="Add to cart"
                        >
                          {isAdding ? <Loader size={13} className="sb-add-spinner" /> : <ShoppingCart size={13} />}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {detailTarget !== null && (
        <FoodDetailModal
          foodId={detailTarget}
          onClose={() => setDetailTarget(null)}
          showNotification={showNotification}
          onCartChange={onCartChange}
        />
      )}
    </div>
  );
};

export default SearchBar;
